import React, { useEffect, useState } from "react";
import { X, BookmarkSimple, ShieldCheck, Info, BookOpen, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = {
  star: "Star", planet: "Planet", dwarf_planet: "Dwarf Planet",
  moon: "Moon", deep_space: "Deep Space", galaxy: "Galaxy",
  nebula: "Nebula", black_hole: "Black Hole", cluster: "Star Cluster",
};

export default function ObjectPanel({ objectId, onClose, onDeepDive, onViewClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!objectId) return;
    setLoading(true);
    api.get(`/objects/${objectId}`)
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [objectId]);

  const save = async () => {
    try {
      await api.post("/saved-views", { object_id: objectId });
      toast.success(`${data.object.name} saved to your collection`);
    } catch (e) {
      toast.error("Could not save view");
    }
  };

  if (!objectId) return null;
  const obj = data?.object;

  return (
    <div
      data-testid="object-panel"
      className="fixed left-0 top-0 h-full w-full sm:w-[400px] z-30 glass slide-in-left flex flex-col"
    >
      {loading || !obj ? (
        <div className="flex-1 flex items-center justify-center text-tel text-xs text-white/40">Loading telemetry…</div>
      ) : (
        <>
          <div className="relative flex-shrink-0">
            {obj.image && (
              <div className="relative h-44">
                <img src={obj.image} alt={obj.name} className="w-full h-full object-cover" data-testid="panel-image" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(10,10,15,1), transparent 70%)" }} />
              </div>
            )}
            <button data-testid="panel-close" onClick={onClose} className="absolute right-5 top-5 w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-colors" style={{ background: "rgba(3,3,5,0.5)" }}>
              <X size={20} />
            </button>
            <div className={`px-7 pb-5 ${obj.image ? "-mt-10 relative" : "pt-7"} border-b border-white/10`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-3 h-3 rounded-full" style={{ background: obj.color, boxShadow: `0 0 12px ${obj.color}` }} />
                <span className="text-tel text-[10px] text-cyan-400/80">{TYPE_LABEL[obj.type] || obj.type}</span>
              </div>
              <h2 className="font-display font-bold text-3xl tracking-tighter leading-none">{obj.name}</h2>
              <p className="text-white/50 text-sm mt-2">{obj.tagline}</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-7 py-6 space-y-7">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={15} weight="duotone" color="#00D8FF" />
                <span className="text-tel text-[9px] text-white/50">Verified Data</span>
              </div>
              <div className="grid grid-cols-2 gap-px bg-white/5 rounded-lg overflow-hidden">
                {Object.entries(obj.facts).map(([k, v]) => (
                  <div key={k} className="bg-[#08080d] p-3.5" style={{ borderLeft: "2px solid rgba(0,216,255,0.4)" }}>
                    <div className="text-tel text-[8px] text-white/40 mb-1">{k}</div>
                    <div className="font-mono-tel text-[13px] text-white/90 leading-tight">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <Info size={15} weight="duotone" color="#A1A1AA" />
                <span className="text-tel text-[9px] text-white/50">Overview</span>
              </div>
              <p className="text-white/75 text-sm leading-relaxed">{obj.description}</p>
            </div>

            {data.children?.length > 0 && (
              <div>
                <span className="text-tel text-[9px] text-white/50">Notable Satellites</span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {data.children.map((c) => (
                    <span key={c.id} className="text-xs px-3 py-1.5 rounded-full border border-white/12 text-white/70">
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="px-7 py-5 border-t border-white/10 space-y-3">
            <button
              data-testid="panel-deepdive"
              onClick={() => onDeepDive(obj)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 transition-colors"
            >
              <BookOpen size={17} weight="fill" /> Astrophysical Analysis
            </button>
            {obj.realm === "solar" && onViewClose && (
              <button
                data-testid="panel-viewclose"
                onClick={() => onViewClose(obj)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm border border-white/15 text-white/80 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors"
              >
                <MagnifyingGlass size={16} weight="duotone" /> View Up Close
              </button>
            )}
            {user && (
              <button
                data-testid="panel-save"
                onClick={save}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm border border-white/15 text-white/80 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors"
                title="Save view"
              >
                <BookmarkSimple size={18} weight="duotone" /> Save to Collection
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
